import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { concatMap, debounceTime, Subject } from 'rxjs';
import { ModulesEnum } from '../enums/modules.enum';
import {
  LIST_SORT_SETTINGS_KEY,
  listSortPreferencesToBagValue,
  parseListSortPreferences,
} from '../mappers/list-sort-preferences.mapper';
import { UserAppSettingsStore } from './user-app-settings.store';

type ListSortPreferencesState = {
  preferences: Partial<Record<ModulesEnum, string>>;
};

const DEFAULT_STATE: ListSortPreferencesState = {
  preferences: {},
};

export const ListSortPreferencesStore = signalStore(
  { providedIn: 'root' },
  withState(DEFAULT_STATE),
  withMethods(
    (
      store,
      userAppSettingsStore = inject(UserAppSettingsStore),
      destroyRef = inject(DestroyRef),
    ) => {
      const persist$ = new Subject<void>();
      persist$
        .pipe(
          debounceTime(300),
          concatMap(() => {
            const settingsValues = {
              ...(userAppSettingsStore.userAppSettings?.()?.settingsValues ??
                {}),
              [LIST_SORT_SETTINGS_KEY]: listSortPreferencesToBagValue(
                store.preferences(),
              ),
            };
            return userAppSettingsStore.replaceSettings({ settingsValues });
          }),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe();

      const persist = () => persist$.next();

      return {
        sortOptionId(module: ModulesEnum): string | undefined {
          return store.preferences()[module];
        },
        setSortOptionId(module: ModulesEnum, sortOptionId: string): void {
          patchState(store, {
            preferences: {
              ...store.preferences(),
              [module]: sortOptionId,
            },
          });
          persist();
        },
        hydrateFromBag(
          settingsValues: Record<string, unknown> | undefined,
        ): void {
          patchState(store, {
            preferences: parseListSortPreferences(
              settingsValues?.[LIST_SORT_SETTINGS_KEY],
            ),
          });
        },
      };
    },
  ),
);
